// Find a topic, in every Move to topic (D172 of documentation/pr-plans/insights-simplify-plan.md): the activity
// tables' dialog (insights-activity-table/row-actions.js) and Sort my searches' dialog (sort-my-searches/topic-choices);
// Search ideas' ⋯ offers Not relevant only since D216. The box is always there and filters as a person types; a group
// whose topics all hide hides with them, and the count beside a group is the topics that still show. Whenever no topic
// is named exactly what was typed, even while others match part of it ("superset" beside Superset dashboards), the
// dialog offers Create topic and move here, in a search intent picked beside it.
//
// The keyboard: Down, or Enter, in the box picks the first topic that shows and moves focus to it; Up and Down (and
// Left and Right) then step through the rows that show, wrapping, and pick the one they land on (stepPick(): a radio
// group's own arrows land on a row Find a topic hid as well); Enter on a row presses the dialog's confirm
// ([data-topic-find-confirm]).

/** The search intents a new topic may take (MoveTopicChoices::NEW_TOPIC_STAGES), never Not sorted. */
export const NEW_TOPIC_STAGES = Object.freeze(["not_relevant", "bottom", "middle", "top"]);

/** The search intent Create topic and move here starts on, as sketch 6 does. */
export const DEFAULT_NEW_STAGE = "middle";

/** Whether a topic's name shows for what was typed: any part of it, case aside; nothing typed shows every one. */
export function topicMatches(name, query) {
    const wanted = String(query ?? "").trim().toLocaleLowerCase();

    return wanted === "" || String(name ?? "").toLocaleLowerCase().includes(wanted);
}

/** Whether two names are one topic's: case and runs of spaces aside, as SiteTopic::normalizeName() and the column compare them. */
export function isSameName(name, other) {
    const key = (value) => String(value ?? "").trim().replace(/\s+/gu, " ").toLocaleLowerCase();

    return key(name) !== "" && key(name) === key(other);
}

/** Whether Create topic and move here is offered: something typed that no topic is named exactly. */
export function canCreateNamed(names, query) {
    return String(query ?? "").trim() !== "" && !(Array.isArray(names) ? names : []).some((name) => isSameName(name, query));
}

/** The radios a list draws now, in their order: a hidden row has no box. */
export function shownPicks(root) {
    return Array.from(root?.querySelectorAll?.('input[type="radio"]') ?? []).filter((radio) => radio.getClientRects().length > 0);
}

/** The topic `delta` rows on from the focused one, among those that show, wrapping, takes the pick and focus. */
export function stepPick(root, delta) {
    const picks = shownPicks(root);

    if (picks.length === 0) {
        return null;
    }

    const at = picks.indexOf(typeof document === "undefined" ? null : document.activeElement);
    const next = picks[at === -1 ? 0 : (at + delta + picks.length) % picks.length];

    next.focus();

    if (!next.checked) {
        next.click();
    }

    return next;
}

/** The first topic that shows takes the pick and focus, or nothing when none shows. */
export function pickFirst(root) {
    const first = shownPicks(root)[0] ?? null;

    first?.focus();

    if (first !== null && !first.checked) {
        first.click();
    }

    return first;
}

/**
 * The Alpine half the server-drawn lists share (Sort my searches' dialog, Search ideas' ⋯), merged over `extra`,
 * the caller's own state. Each row's name and each group's names come from the markup.
 */
export default function InsightsTopicFind(extra = {}) {
    return {
        ...extra,
        query: "",
        newStage: DEFAULT_NEW_STAGE,

        matches(name) {
            return topicMatches(name, this.query);
        },

        shownOf(names) {
            return (Array.isArray(names) ? names : []).filter((name) => this.matches(name)).length;
        },

        // A group's count of the topics that still show, in its words for one (`one`) or several (`many`), :count the number.
        countWords(names, one, many) {
            const count = this.shownOf(names);

            return (count === 1 ? one : many).replace(":count", count.toLocaleString());
        },

        // `names` are every topic's own name (and Not relevant for its catch-all), compared whole, never in part.
        canCreate(names) {
            return canCreateNamed(names, this.query);
        },

        newTopicName() {
            return this.query.trim();
        },

        newTopic() {
            return { name: this.newTopicName(), stage: NEW_TOPIC_STAGES.includes(this.newStage) ? this.newStage : DEFAULT_NEW_STAGE };
        },

        pickFirst() {
            return pickFirst(this.$root);
        },

        stepPick(delta) {
            return stepPick(this.$root, delta);
        },

        confirmPick() {
            this.$root.closest('[role="dialog"]')?.querySelector("[data-topic-find-confirm]")?.click();
        },
    };
}
